import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BillingBlockPayload } from '../types';
import {
  BillingBlockedContext,
  BillingBlockedError,
  emitBillingBlocked,
  notifyBillingBlockedError,
} from '../services/BillingBlockedError';
import { clearBillingAccessCache } from '../hooks/useBillingAccess';
import { BillingRequiredDialog } from './BillingRequiredDialog';

interface BillingInterruptionState {
  billingBlock: BillingBlockPayload;
  context: BillingBlockedContext;
}

export interface BillingInterruptionContextValue {
  showBillingBlock: (billingBlock: BillingBlockPayload, context?: BillingBlockedContext) => void;
  handleApiError: (error: unknown, context?: BillingBlockedContext) => boolean;
  refreshAndShow: (projectId: string | undefined, billingBlock: BillingBlockPayload, context?: BillingBlockedContext) => void;
}

const BillingInterruptionContext = createContext<BillingInterruptionContextValue | null>(null);

export function useBillingInterruption(): BillingInterruptionContextValue {
  const value = useContext(BillingInterruptionContext);
  if (!value) {
    return {
      showBillingBlock: emitBillingBlocked,
      handleApiError: notifyBillingBlockedError,
      refreshAndShow: (projectId, billingBlock, context) => {
        clearBillingAccessCache(projectId);
        emitBillingBlocked(billingBlock, context);
      },
    };
  }
  return value;
}

export function BillingInterruptionProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const navigate = useNavigate();
  const [state, setState] = useState<BillingInterruptionState | null>(null);
  const lastKeyRef = useRef<string>('');

  const showBillingBlock = useCallback((billingBlock: BillingBlockPayload, context: BillingBlockedContext = {}) => {
    const key = JSON.stringify({ billingBlock, context });
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    if (context.projectId) {
      clearBillingAccessCache(context.projectId);
    }
    setState({ billingBlock, context });
  }, []);

  const handleApiError = useCallback((error: unknown, context: BillingBlockedContext = {}) => {
    if (error instanceof BillingBlockedError) {
      showBillingBlock(error.billingBlock, context);
      return true;
    }
    return false;
  }, [showBillingBlock]);

  const refreshAndShow = useCallback((
    projectId: string | undefined,
    billingBlock: BillingBlockPayload,
    context: BillingBlockedContext = {},
  ) => {
    clearBillingAccessCache(projectId);
    showBillingBlock(billingBlock, { ...context, projectId: context.projectId || projectId });
  }, [showBillingBlock]);

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{
        billingBlock?: BillingBlockPayload;
        context?: BillingBlockedContext;
      }>).detail;
      if (!detail?.billingBlock) return;
      showBillingBlock(detail.billingBlock, detail.context || {});
    };
    window.addEventListener('segmently:billing-blocked', listener);
    return () => window.removeEventListener('segmently:billing-blocked', listener);
  }, [showBillingBlock]);

  const close = useCallback(() => {
    setState(null);
    window.setTimeout(() => {
      lastKeyRef.current = '';
    }, 250);
  }, []);

  const primary = useCallback(() => {
    const projectId = state?.context.projectId;
    if (projectId && state.billingBlock.canSelfServe !== false) {
      navigate(`/project/${encodeURIComponent(projectId)}/billing`);
    }
    close();
  }, [close, navigate, state]);

  const value = useMemo(() => ({
    showBillingBlock,
    handleApiError,
    refreshAndShow,
  }), [handleApiError, refreshAndShow, showBillingBlock]);

  return (
    <BillingInterruptionContext.Provider value={value}>
      {children}
      <BillingRequiredDialog
        open={Boolean(state)}
        billingBlock={state?.billingBlock || null}
        actionLabel={state?.context.actionLabel}
        onClose={close}
        onPrimary={state?.billingBlock.canSelfServe === false ? undefined : primary}
      />
    </BillingInterruptionContext.Provider>
  );
}
